import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosHttp from "services/axiosHttp";

export const fetchFolderList = createAsyncThunk("fetchFolderList", async ({ space_id }) => {
	const response = await axiosHttp.get("/dms/folder", {
		params: {
			space_id: space_id,
		},
	});
	return response.data;
});

export const createFolder = createAsyncThunk("createFolder", async (data) => {
	// Only include parent_id in the payload if it's provided (for subfolders)
	const payload = {
		name: data.name,
		space_id: data.space_id,
	};

	if (data.parent_id) {
		payload.parent_id = data.parent_id;
	}

	const response = await axiosHttp.post("/dms/folder", payload);
	return response.data;
});

export const updateFolder = createAsyncThunk("updateFolder", async (data) => {
	const response = await axiosHttp.put("/dms/folder", data);
	return response.data;
});

export const renameFolder = createAsyncThunk("renameFolder", async ({ id, name, space_id }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.patch(`/dms/folder/${id}/rename`, { name, space_id });
		return response.data;
	} catch (error) {
		return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to rename folder");
	}
});

export const deleteFolder = createAsyncThunk("deleteFolder", async (id) => {
	const response = await axiosHttp.post(`/dms/delete-folder/${id}`);
	return response.data;
});

const renderTreeData = (data) => {
	// Create a map to store all items by their ID for quick lookup
	const itemMap = {};
	const tree = [];

	// First pass: create all items and store them in the map
	data.forEach((item) => {
		itemMap[item.id] = {
			title: item.name,
			key: `${item.id}`,
			id: item.id,
			parent_id: item.parent_id,
			children: [],
		};
	});

	// Second pass: build the tree structure
	data.forEach((item) => {
		const treeItem = itemMap[item.id];

		if (item.parent_id === null || item.parent_id === undefined) {
			// Root level item
			tree.push(treeItem);
		} else {
			// Child item - find parent and add to its children
			const parent = itemMap[item.parent_id];
			if (parent) {
				// Update the key to include parent relationship
				treeItem.key = `${item.parent_id}-${item.id}`;
				parent.children.push(treeItem);
			}
		}
	});

	return tree;
};

const folderSlice = createSlice({
	name: "dmsFolderSlice",
	initialState: {
		list: {
			data: null,
			isLoading: false,
			isError: false,
			error: null,
		},
		selected: null,
		create: {
			isLoading: false,
			isError: false,
			error: null,
			data: null,
			isCreating: false,
			folderName: null,
			space_id: null,
		},
		update: {
			isLoading: false,
			isError: false,
			error: null,
			data: null,
			isUpdating: false,
		},
		delete: {
			isLoading: false,
			isError: false,
			error: null,
			data: null,
		},
	},
	reducers: {
		resetFolderState: (state) => {
			state.list.isLoading = false;
			state.list.isError = false;
			state.list.error = null;
			state.create.data = null;
			state.create.isLoading = false;
			state.create.isError = false;
			state.create.error = null;
			state.update.data = null;
			state.update.isLoading = false;
			state.update.isError = false;
			state.update.error = null;
			state.delete.data = null;
			state.delete.isLoading = false;
			state.delete.isError = false;
			state.delete.error = null;
		},
		setIsCreating: (state, action) => {
			state.create.isCreating = action.payload;
		},
		setIsUpdating: (state, action) => {
			state.update.isUpdating = action.payload;
		},
		setFolderName: (state, action) => {
			state.create.folderName = action.payload;
		},
		setSpaceId: (state, action) => {
			state.create.space_id = action.payload;
		},
		setSelectedFolder: (state, action) => {
			state.selected = action.payload;
		},
		resetSelectedFolder: (state) => {
			state.selected = null;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchFolderList.pending, (state, action) => {
			state.list.isLoading = true;
			state.list.isError = false;
			state.list.error = null;
		});
		builder.addCase(fetchFolderList.fulfilled, (state, action) => {
			state.list.isLoading = false;
			state.list.isError = false;
			state.list.error = null;
			state.list.data = renderTreeData(action.payload);
		});
		builder.addCase(fetchFolderList.rejected, (state, action) => {
			state.list.isLoading = false;
			state.list.isError = true;
			state.list.error = action.payload;
		});

		builder.addCase(createFolder.pending, (state, action) => {
			state.create.isLoading = true;
			state.create.isError = false;
			state.create.error = null;
		});
		builder.addCase(createFolder.fulfilled, (state, action) => {
			state.create.isLoading = false;
			state.create.isError = false;
			state.create.error = null;
			state.create.data = action.payload;

			state.create.isCreating = false;
			state.create.folderName = null;
			// Don't reset space_id anymore
		});
		builder.addCase(createFolder.rejected, (state, action) => {
			state.create.isLoading = false;
			state.create.isError = true;
			state.create.error = action.payload;
		});
		builder.addCase(updateFolder.pending, (state, action) => {
			state.update.isLoading = true;
			state.update.isError = false;
			state.update.error = null;
		});
		builder.addCase(updateFolder.fulfilled, (state, action) => {
			state.update.isLoading = false;
			state.update.isError = false;
			state.update.error = null;
			state.update.data = action.payload;
		});
		builder.addCase(updateFolder.rejected, (state, action) => {
			state.update.isLoading = false;
			state.update.isError = true;
			state.update.error = action.payload;
		});
		builder.addCase(renameFolder.pending, (state, action) => {
			state.update.isLoading = true;
			state.update.isError = false;
			state.update.error = null;
		});
		builder.addCase(renameFolder.fulfilled, (state, action) => {
			state.update.isLoading = false;
			state.update.isError = false;
			state.update.error = null;
			state.update.data = action.payload;
		});
		builder.addCase(renameFolder.rejected, (state, action) => {
			state.update.isLoading = false;
			state.update.isError = true;
			state.update.error = action.payload;
		});
		builder.addCase(deleteFolder.pending, (state, action) => {
			state.delete.isLoading = true;
			state.delete.isError = false;
			state.delete.error = null;
		});
		builder.addCase(deleteFolder.fulfilled, (state, action) => {
			state.delete.isLoading = false;
			state.delete.isError = false;
			state.delete.error = null;
			state.delete.data = action.payload;
		});
		builder.addCase(deleteFolder.rejected, (state, action) => {
			state.delete.isLoading = false;
			state.delete.isError = true;
			state.delete.error = action.payload;
		});
	},
});

export const { resetFolderState, setIsCreating, setIsUpdating, setFolderName, setSpaceId, setSelectedFolder } = folderSlice.actions;
export default folderSlice.reducer;
